import React, { ReactNode, useState } from 'react'
import { Box, Grid } from '@mui/material'
import Typography from '@mui/material/Typography'
import { Fee as Detail } from '../../../../types/fee'
import { Payment } from '../../../../types/payment'
import { FeeConfig } from '../../../../types/fee'
import { toLocalizedNumber } from '../../../../utils/helpers'
import { AnimatePresence } from 'framer-motion'
import AnimatedExpandMoreIcon from '../../../../base/animated/AnimatedExpandMoreIcon/AnimatedExpandMoreIcon'
import AnimatedAccordionWrapper from '../../../../base/animated/AnimatedAccordionWrapper/AnimatedAccordionWrapper'

const DETAIL_TYPE_TO_TEXT: { [key: string]: string } = {
  PLASTIQ_SERVICE_FEE: 'Base Fee',
  PLASTIQ_CROSS_BORDER_FEE: 'Cross Border Fee',
  EXPEDITED_PROCESSING_FEE: 'Expedited Processing Fee',
  DELIVERY_FEE: 'Delivery Fee',
  EXPEDITED_HANDLING_FEE: 'Delivery Fee',
  // TODO: See if below fee mapping are needed
  // they are not found in artemis: https://gitlab.com/plastiq/artemis/-/blob/dev/artemis/constants/payment.js#L31
  EXPEDITED_BASE_FEE: 'Expedited Processing Fee',
  PLASTIQ_INTL_WIRE_FEE: 'Delivery Fee',
  EXPEDITED_BANK_FEE: 'Delivery Fee',
  PLASTIQ_US_WIRE_FEE: 'Delivery Fee',
  OTHER: 'Other Fee',
}

enum PaymentRowVariant {
  STANDARD = 'STANDARD', // Default
  DETAIL = 'DETAIL',
  EMPHASIZED = 'EMPHASIZED',
}

const copyStyle = {
  [PaymentRowVariant.STANDARD]: {
    label: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    amount: {
      fontSize: '1.25rem',
      fontWeight: 400,
      lineHeight: 1.2,
    },
  },
  [PaymentRowVariant.DETAIL]: {
    label: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.714,
      color: '#00000080',
    },
    amount: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.714,
      color: '#00000080',
    },
  },
  [PaymentRowVariant.EMPHASIZED]: {
    label: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    amount: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1,
    },
  },
}

export interface PaymentRowProps {
  label: string
  amount: string
  currency: string
  details?: Array<Detail>
  type?: PaymentRowVariant
  rate?: string
}

const PaymentRow = (props: PaymentRowProps) => {
  const {
    details,
    type = PaymentRowVariant.STANDARD,
    currency,
    amount,
    label,
    rate,
  } = props
  const isDetailsExist = Boolean(details?.length)
  const [isDetailsShown, setIsDetailsShown] = useState(false)
  return (
    <>
      <PaymentRowContainer isDetailsShown={isDetailsShown} {...props}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            ...(isDetailsExist ? { cursor: 'pointer' } : {}),
          }}
          onClick={() => isDetailsExist && setIsDetailsShown(!isDetailsShown)}
        >
          <Typography
            sx={{
              ...copyStyle[type]?.label,
            }}
          >
            {label} {rate && `(${rate})`}
          </Typography>
          {isDetailsExist && (
            <AnimatedExpandMoreIcon
              animate={{
                rotate: isDetailsShown ? 180 : 0,
                transition: { duration: 0.2 },
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {type === PaymentRowVariant.EMPHASIZED && currency && (
            <Grid item>
              <Typography
                sx={{
                  color: '#00000066',
                  fontSize: '0.75rem',
                  lineHeight: 2,
                  fontWeight: 400,
                }}
              >
                {currency}
              </Typography>
            </Grid>
          )}
          <Typography
            sx={theme => ({
              marginLeft: theme.spacing(1),
              ...copyStyle[type]?.amount,
            })}
          >
            {amount}
          </Typography>
        </Box>
      </PaymentRowContainer>
      <AnimatePresence initial={false}>
        {isDetailsExist && isDetailsShown && (
          <AnimatedAccordionWrapper>
            {details
              ?.filter(detail => detail?.amount?.value > 0)
              .map(detail => (
                <DetailRow
                  key={detail.type}
                  detail={detail}
                  currency={props.currency}
                />
              ))}
          </AnimatedAccordionWrapper>
        )}
      </AnimatePresence>
    </>
  )
}

export interface PaymentRowContainerProps extends PaymentRowProps {
  children: ReactNode
  isDetailsShown: boolean
}

const PaymentRowContainer = (props: PaymentRowContainerProps) => (
  <Grid
    sx={theme => ({
      marginBottom: theme.spacing(
        props.type === PaymentRowVariant.DETAIL || props.isDetailsShown ? 0 : 1,
      ),
      color: '#000000B2',
      ...(props.type === PaymentRowVariant.EMPHASIZED
        ? {
            padding: theme.spacing(1.5, 0, 0),
            marginBottom: 0,
            marginTop: theme.spacing(0.5),
            borderTop: '1px dashed #0000001A',
            color: '#000000CC',
          }
        : {}),
    })}
    item
    justifyContent='space-between'
    container
  >
    {props.children}
  </Grid>
)

interface DetailRowProps {
  detail: Detail
  currency: string
}

const DetailRow = (props: DetailRowProps) => (
  <PaymentRow
    type={PaymentRowVariant.DETAIL}
    label={DETAIL_TYPE_TO_TEXT[props.detail.type]}
    amount={toLocalizedNumber(props.detail.amount.value, props.currency)}
    rate={props.detail.rate.includes('%') ? props.detail.rate : undefined}
    currency={props.currency}
  />
)

export interface PaymentAmountProps {
  payment: Payment
  onClickEdit?: Function
  editAmount?: boolean
  config?: {
    fee: FeeConfig
  }
}

const PaymentAmount = (props: PaymentAmountProps) => {
  const { fees, sourceAmount, targetAmount } = props.payment || {}
  const feeTotal = fees?.reduce((p, c) => p + c?.amount?.value, 0) || 0
  return (
    <Grid container>
      <PaymentRow
        label='Payment Amount'
        amount={toLocalizedNumber(targetAmount.value, targetAmount.currency)}
        currency={targetAmount.currency}
      />
      <PaymentRow
        label='Fees'
        amount={toLocalizedNumber(feeTotal, sourceAmount.currency)}
        details={fees}
        currency={sourceAmount.currency}
      />
      <PaymentRow
        label='Total'
        amount={toLocalizedNumber(sourceAmount.value, sourceAmount.currency)}
        type={PaymentRowVariant.EMPHASIZED}
        currency={sourceAmount.currency}
      />
    </Grid>
  )
}

export default PaymentAmount
